import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import WhySkyhook from '@website/components/about/WhySkyhook';
import { trackGTMEvent } from '@website/components/analytics/trackEvent';
import GroupsAvailability from '@website/components/trips/TripsPage/groups/GroupAvailability';
import ListItineraryItems from '@website/components/trips/TripsPage/Itinerary/ListItineraryItems';
import ListTripFaqs from '@website/components/trips/TripsPage/ListTripFaqs';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import PriceFormat from '@website/components/utilities/PriceFormat';
import { TripsPageDocument } from '@website/types/graphqlOperations';
import Link from 'next/link';
import {
  Alert,
  Button,
  Col,
  Container,
  Nav,
  Navbar,
  Row,
} from 'react-bootstrap';
import { AiOutlineMinus } from 'react-icons/ai';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { buildURL } from 'react-imgix';
import { Rating } from 'react-simple-star-rating';
import { getImageUrl } from '../../media/utils/imgix';
import SeoMetaTags from '../../seoMetaTags';
import NotFound from '../../utilities/NotFound';
import WishlistToggleButton from '../../Wishlist/WishlistToggleButton';
import AskForHelp from './AskForHelp';
import { RelatedTrips } from './Recomended/RelatedTrips';
import Reviews from './reviews/Reviews';
import TripHeader from './TripHeader';
import TripSchemaMarkup from './TripMarkup';
import styles from './TripsPageContent.module.scss';
import TripVideoHighlights from './TripVideoHighlights';
import TripsPageHostInfo from './utils/TripsPageHostInfo';

interface Props {
  urlSlug: string;
}

export default function TripsPageContent({ urlSlug }: Props) {
  const { loading, data, error } = useQuery(TripsPageDocument, {
    variables: {
      urlSlug,
    },
  });

  const trip = data?.tripByURL;
  const faqs = data?.tripByURL.faqs.items || [];
  const itineraryItems = data?.tripByURL.itinerary.items || [];
  const groups = data?.tripByURL.groups.items ?? [];
  const reviewsSummary = data?.tripByURL.reviewsSummary;

  // When a customer visits a specific group date page for a trip, track this as a product variant view

  useEffect(() => {
    if (!trip || !groups) return;

    trackGTMEvent('view_item', {
      // send_to: 'AW-859214494/OuhMCLuD1fsYEJ6l2pkD',
      currency: groups[0]?.guestPrice.currency,
      price: (groups[0]?.guestPrice?.amountZD || 0) / 100,
      items: [
        {
          item_id: trip.id,
          item_name: trip.title,
          item_brand: trip.host.name,
          image_url: buildURL(`${getImageUrl(trip.image?.file?.key)}`),
          price: (groups[0]?.guestPrice?.amountZD || 0) / 100,
        },
      ],
    });
  }, [trip]);

  if (loading && !data) {
    return <LoadingPage />;
  }

  // Show 404 if not found
  if (error?.message?.includes('Cannot return null')) {
    return <NotFound />;
  }

  // Show errors except if we have a network error from polling
  if (error && !data) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <SeoMetaTags
        title={
          data.tripByURL.pageSettings?.seo?.metaTitle ?? data.tripByURL.title
        }
        description={
          data.tripByURL.pageSettings?.seo?.metaDescription ??
          data.tripByURL.description
        }
        imageKey={data.tripByURL.image?.file?.key}
        showWebsiteName={false}
      />

      <TripSchemaMarkup trip={trip} />

      <div className={styles.page}>
        <TripHeader trip={trip} />

        {data.tripByURL.pageSettings?.discontinued?.isDiscontinued && (
          <Container className="g-0 py-3">
            <Alert variant="info text-center">
              <div className="fw-bold d-block">OOPS!</div>
              <div className="fw-bold d-block">
                We are no longer running this trip...
              </div>
              {trip.pageSettings?.discontinued?.recommendedTrips?.length >
                0 && (
                <div className="d-block">
                  But you may enjoy:{' '}
                  {trip.pageSettings.discontinued.recommendedTrips.map(
                    (recommendedTrip, index) => (
                      <>
                        <Link
                          key={recommendedTrip.url}
                          href={recommendedTrip.url}
                          className="fw-bold"
                        >
                          {recommendedTrip.title}
                        </Link>
                        {index <
                          trip.pageSettings.discontinued.recommendedTrips
                            .length -
                            1 && <span className="fw-normal"> or </span>}
                      </>
                    ),
                  )}
                </div>
              )}
            </Alert>
          </Container>
        )}

        <TripVideoHighlights trip={trip} />

        <Container>
          <h2 className="mb-3" data-cy="itinerary-item-header">
            Itinerary
          </h2>

          <ListItineraryItems itineraryItems={itineraryItems} />
        </Container>

        <Container className="g-0">
          <Alert
            variant="success"
            className="pt-4 pb-5 mt-2 mb-4 bg-success text-white"
            id="booknow"
            style={{ borderRadius: 0 }}
          >
            <h2 className="mb-2">Book Now</h2>
            {trip.active === 'ACTIVE' ? (
              <GroupsAvailability groups={groups} trip={trip} />
            ) : (
              <p>No dates currently available to book</p>
            )}
          </Alert>
        </Container>

        <Container className="g-0 pt-3">
          {(trip.allowPrivateGroups ||
            trip.pageSettings?.recommendedTrips?.length > 0) && (
            <Alert variant="info">
              {trip.allowPrivateGroups && (
                <p>
                  <span className="fw-bold">Don't see your date?</span> We may
                  be able to open a new group or setup a private trip if there
                  are 2 or more people in your party.{' '}
                  <Link href={`/help/contact`} passHref legacyBehavior>
                    <Alert.Link className="text-primary">
                      {' '}
                      Contact us
                    </Alert.Link>
                  </Link>
                  .
                </p>
              )}

              {trip.pageSettings?.recommendedTrips?.length > 0 && (
                <p className="fw-bold">
                  You may also like:{' '}
                  {trip.pageSettings?.recommendedTrips
                    .map((recommendedTrip) => (
                      <Link
                        href={`${recommendedTrip.url}`}
                        key={recommendedTrip.url}
                        target="_blank"
                      >
                        {recommendedTrip.title}
                      </Link>
                    ))
                    // @ts-expect-error
                    .reduce((prev, curr) => [
                      prev,
                      // eslint-disable-next-line react/jsx-key
                      <span className="fw-normal"> or </span>,
                      curr,
                    ])}
                </p>
              )}
            </Alert>
          )}
        </Container>

        {((trip.includes && trip.includes?.length > 0) ||
          (trip.excludes && trip.excludes?.length > 0)) && (
          <Container className="my-4">
            <h2 className="mb-3">What's Included</h2>
            <Row>
              {trip.includes?.length > 0 && (
                <Col xs={12} md={6}>
                  <h5 className="mb-4 h-6">Included</h5>
                  <ul className="list-unstyled">
                    {trip.includes.map((i) => (
                      <li key={i}>
                        <IoMdCheckmarkCircleOutline
                          className="me-1 text-success"
                          size={20}
                        />
                        {i}
                      </li>
                    ))}
                  </ul>
                </Col>
              )}

              {trip.excludes?.length > 0 && (
                <Col xs={12} md={6}>
                  <h5 className="mb-4 h-6">Not Included</h5>
                  <ul className="list-unstyled">
                    {trip.excludes.map((i) => (
                      <li key={i}>
                        <AiOutlineMinus className="me-1 text-danger" />
                        {i}
                      </li>
                    ))}
                  </ul>
                </Col>
              )}
            </Row>
          </Container>
        )}

        <AskForHelp
          tripTitle={trip.title}
          link={`/help/contact`}
          type="in-content"
        />

        <Container id="reviews">
          {reviewsSummary.reviewCount > 0 && (
            <Reviews
              reviews={trip.reviews}
              reviewsSummary={trip.reviewsSummary}
              tripID={trip.id}
              host={trip.host}
            />
          )}
        </Container>

        <Container>
          <ListTripFaqs faqs={faqs} notice={trip.notice} />
        </Container>

        <Container id="contact-guide">
          <TripsPageHostInfo host={trip.host} tripTitle={trip.title} />
        </Container>

        {trip?.groups?.items?.length > 0 && (
          <Container>
            <RelatedTrips
              countryCode={trip.address.country}
              activities={trip.activities}
            />
          </Container>
        )}

        <WhySkyhook />

        {trip.active === 'ACTIVE' && (
          <Navbar
            fixed="bottom"
            bg="white"
            className="border-top"
            id="book-now-navbar"
          >
            <Container data-cy="trip-banner">
              <Nav className="me-auto">
                <div className="price-reviews">
                  {data.tripByURL.minimumPrice?.amountZD && (
                    <div>
                      <p style={{ fontSize: 8, margin: '0 0 -4px' }}>
                        <strong>FROM</strong>
                      </p>

                      {trip?.minimumPrice?.priceBeforeDiscount && (
                        <span className="text-sm me-1 text-decoration-line-through">
                          <PriceFormat
                            amount={
                              trip.minimumPrice.priceBeforeDiscount?.amountZD
                            }
                            currencyIso={
                              trip.minimumPrice.priceBeforeDiscount.currency
                            }
                            convert
                            roundDollar
                            roundAmountOff
                            hideCurrency
                          />
                        </span>
                      )}

                      <strong
                        className={
                          trip.minimumPrice?.priceBeforeDiscount
                            ? 'text-secondary'
                            : ''
                        }
                      >
                        <PriceFormat
                          amount={data.tripByURL.minimumPrice?.amountZD}
                          currencyIso={data.tripByURL.minimumPrice?.currency}
                          convert
                          roundDollar
                          roundAmountOff
                          hideCurrency
                        />
                      </strong>
                    </div>
                  )}
                  {reviewsSummary.reviewCount > 0 && (
                    <div className="navbar-stars">
                      <Rating
                        initialValue={reviewsSummary?.fiveStarRatingAverage}
                        readonly
                        allowFraction
                        size={16}
                        fillColor="#4e66f8"
                      />
                    </div>
                  )}
                </div>
              </Nav>

              <div className="px-3">
                <WishlistToggleButton tripId={trip.id} isTripsPage />
              </div>
              <AskForHelp
                tripTitle={trip.title}
                link={`/help/contact`}
                type="footer"
              />

              <Button
                variant="primary"
                onClick={() => {
                  document.getElementById('booknow').scrollIntoView();
                }}
              >
                Book Now
              </Button>
            </Container>
          </Navbar>
        )}
      </div>
    </>
  );
}
